import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuth } from './auth/AuthContext'
import Login from './views/Login'
import Home from './views/Home'
import MyWork from './views/MyWork'
import AppLayout from './shell/AppLayout'
import ProjectLayout from './views/project/ProjectLayout'
import ProjectDashboard from './views/project/Dashboard'
import ProjectBacklog from './views/project/Backlog'
import ProjectEpics from './views/project/Epics'
import ProjectBoard from './views/project/Board'
import ProjectSprints from './views/project/Sprints'
import ProjectTimeline from './views/project/Timeline'
import ProjectTeam from './views/project/Team'
import Calendar from './views/Calendar'
import Settings from './views/Settings'
import NewProject from './views/NewProject'
// Reports pulls in recharts (~400 KB) — lazy-load so dashboard stays snappy.
const ProjectReports = lazy(() => import('./views/project/Reports'))

function LazyView({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-border border-t-accent animate-spin" />
      </div>
    }>
      {children}
    </Suspense>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/" element={<Home />} />
        <Route path="/my-work" element={<MyWork />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/projects/new" element={<NewProject />} />
        <Route path="/projects/:projectKey" element={<ProjectLayout />}>
          <Route index element={<ProjectDashboard />} />
          <Route path="backlog"  element={<ProjectBacklog />} />
          <Route path="epics"    element={<ProjectEpics />} />
          <Route path="board"    element={<ProjectBoard />} />
          <Route path="sprints"  element={<ProjectSprints />} />
          <Route path="calendar" element={<ProjectCalendarRedirect />} />
          <Route path="timeline" element={<ProjectTimeline />} />
          <Route path="team"     element={<ProjectTeam />} />
          <Route path="reports"  element={<LazyView><ProjectReports /></LazyView>} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

/** The calendar is personal now; a project's tab opens it filtered to that project. */
function ProjectCalendarRedirect() {
  const { projectKey } = useParams<{ projectKey: string }>()
  return <Navigate to={`/calendar?project=${encodeURIComponent(projectKey ?? '')}`} replace />
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="h-8 w-8 rounded-full border-2 border-border border-t-accent animate-spin" />
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}
